import type { ReactElement } from 'react';
import { Form } from 'react-router';
import { Button } from '@/components/ui/button';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';
import { SvgApple, SvgGoogle } from '@/components/icons-eataly';
import { cn } from '@/lib/utils';

interface SocialLoginButtonsProps {
    redirectPath?: string;
    formAction?: string;
    variant?: 'default' | 'drawer' | 'iconOnly' | 'compact' | 'page';
}

function formatProviderDisplayName(provider: string, { appleId = false }: { appleId?: boolean } = {}): string {
    const name = provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
    if (appleId && name === 'Apple') {
        return 'Apple ID';
    }
    return name;
}

function ProviderIcon({ provider }: { provider: string }): ReactElement {
    switch (provider.toLowerCase()) {
        case 'apple':
            return <SvgApple className="size-6" aria-hidden />;
        case 'google':
            return <SvgGoogle className="size-6" aria-hidden />;
        default:
            return (
                <span className="text-lg" aria-hidden>
                    ?
                </span>
            );
    }
}

export function SocialLoginButtons({
    redirectPath,
    formAction,
    variant = 'default',
}: SocialLoginButtonsProps = {}): ReactElement | null {
    const config = useConfig();
    const { t } = useTranslation('login');
    const socialIDPs: string[] = [...new Set(config.features.socialLogin.providers)];

    // text template moved to uiStrings.login.continueWithProvider

    if (socialIDPs.length === 0) {
        return null;
    }

    if (variant === 'iconOnly') {
        return (
            <div className="flex gap-6 ">
                {socialIDPs.map((provider) => {
                    return (
                        <Form method="post" action={formAction} key={provider}>
                            <input type="hidden" name="loginMode" value="social" />
                            <input type="hidden" name="provider" value={provider} />
                            {redirectPath && <input type="hidden" name="redirectPath" value={redirectPath} />}
                            <Button
                                type="submit"
                                hierarchy="secondary"
                                className="cursor-pointer"
                                size="icon-large"
                                aria-label={t('continueWithProvider', { provider })}>
                                <ProviderIcon provider={provider} />
                            </Button>
                        </Form>
                    );
                })}
            </div>
        );
    }

    const isDrawer = variant === 'drawer';
    const isCompact = variant === 'compact';
    const isPage = variant === 'page';

    if (isCompact) {
        return (
            <div className="flex items-center gap-2">
                {socialIDPs.map((provider) => {
                    return (
                        <Form method="post" action={formAction} key={provider}>
                            <input type="hidden" name="loginMode" value="social" />
                            <input type="hidden" name="provider" value={provider} />
                            {redirectPath && <input type="hidden" name="redirectPath" value={redirectPath} />}
                            <Button
                                type="submit"
                                hierarchy="secondary"
                                mode="light"
                                size="icon-large"
                                iconOnly
                                className="!size-10 !min-h-10 !min-w-10 !rounded-full"
                                aria-label={t('continueWithProvider', { provider })}
                                iconBefore={<ProviderIcon provider={provider} />}
                            />
                        </Form>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={cn('space-y-3', isDrawer && 'w-full space-y-6', isPage && 'space-y-4')}>
            {!isDrawer && !isPage ? (
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border/60" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">{t('socialOrContinueWith')}</span>
                    </div>
                </div>
            ) : null}

            <div className={cn('grid gap-2', isDrawer && 'gap-6', isPage && 'gap-6')}>
                {socialIDPs.map((provider) => {
                    const providerLabel = formatProviderDisplayName(provider, { appleId: isPage });
                    return (
                        <Form method="post" action={formAction} key={provider}>
                            <input type="hidden" name="loginMode" value="social" />
                            <input type="hidden" name="provider" value={provider} />
                            {redirectPath && <input type="hidden" name="redirectPath" value={redirectPath} />}
                            <Button
                                type="submit"
                                hierarchy="secondary"
                                mode="light"
                                size="large"
                                className={cn('w-full uppercase cursor-pointer', !isDrawer && !isPage && 'normal-case')}
                                iconBefore={<ProviderIcon provider={provider} />}>
                                {isPage
                                    ? t('signInWithProvider', { provider: providerLabel })
                                    : t('continueWithProvider', { provider })}
                            </Button>
                        </Form>
                    );
                })}
            </div>
        </div>
    );
}
